import { useCallback, useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  DeploymentResponseDto,
  DeploymentTemplateResponseDto,
  DropDownDto,
  ProjectCreateDto,
  ProjectResponseDto,
  TaxonomyResponseDto,
} from "../common/ObjectTypes";
import { useTranslation } from "react-i18next";
import {
  getCountries,
  getOrganizationByGuid,
  getProjectOrganizationOptions,
} from "../../api/organizationsApi";
import {
  createProject,
  getProject,
  getProjectByGuid,
  getProjectPermissions,
  getProjectTimezones,
  updateProject,
} from "../../api/projectsApi";
import { MigrationJobDto } from "../../api/migrationsApi";
import MigrateFromWildlifeInsights from "../migrations/MigrateFromWildlifeInsights";
import {
  canOpenWildlifeInsightsMigration,
  migrationLandingProjectIds,
} from "./migrationEntry";
import { toast } from "react-toastify";
import { BeatLoader } from "react-spinners";
import ProjectDetails from "./ProjectDetails";
import { useProfile } from "./../common/ProfileProvider";
import { setSecret } from "../../api/uploadApi";
import {
  DeploymentSearchQueryType,
  SetSecretQueryType,
} from "../common/QueryTypes";
import i18n from "../../i18n";
import { FIELD_LIMITS, lengthError } from "../common/fieldLimits";
import SideMenu from "../common/SideMenu";
import NoAccess from "../common/NoAccess";
import { isForbiddenError } from "../../api/apiUtils";
import ManageProjectUsers from "./ManageProjectUsers";
import ManageProjectDeployments from "./ManageProjectDeployments";
import ManageProjectLocations from "./ManageProjectLocations";
import ManageProjectAiMappings from "./ManageProjectAiMappings";
import ProjectDetailsOverview from "./ProjectDetailsOverview";
import ManageDeploymentDetails from "../deployments/ManageDeploymentDetails";
import {
  getDeploymentTemplateById,
  getDeploymentTemplates,
} from "../../api/deploymentTemplatesApi";
import { getDeployments } from "../../api/deploymentsApi";
import { parseLocalDateStringToDate } from "../common/helpers";
import tailwindClassnames from "../common/TailwindClassnames";
import { formsDiffer, useUnsavedChanges } from "../common/UnsavedChanges";
import { fetchTaxonomyData, getTaxonomyById } from "../../api/taxonomyApi";
import { debounce } from "lodash";
import { searchTaxonomy } from "../../api/taxonomyApi";

type ManageProjectDetailsProps = {
  existingProject: boolean;
};

const ManageProjectDetailsPage: React.FC<ManageProjectDetailsProps> = ({
  existingProject,
}) => {
  const { projectGuid } = useParams();
  const { deploymentGuid } = useParams();
  const { subpage } = useParams();
  const { guid } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // AKL-812. The Wildlife Insights migration opens from this page in both modes: create
  // mode on the New Project form, target-project mode (the second pass) on Edit Project.
  const [migrationOpen, setMigrationOpen] = useState(false);
  // The applied job, kept until the user closes the modal so the report stays readable
  // before the page moves on to the project it created.
  const [appliedMigration, setAppliedMigration] =
    useState<MigrationJobDto | null>(null);
  const [org_id, setOrgId] = useState<number | undefined>();
  const [project_id, setProjectId] = useState<number | undefined>();
  const [perms, setPerms] = useState([]);
  const { profile, profileLoading } = useProfile();
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);
  const [timezones, setTimezones] = useState<string[]>([]);
  // Starts true so the select reads as pending rather than as an empty list on first paint.
  const [timezonesLoading, setTimezonesLoading] = useState(true);
  const [deploymentTemplates, setDeploymentTemplates] = useState<
    DeploymentTemplateResponseDto[]
  >([]);
  const [projectDeployments, setProjectDeployments] = useState<
    DeploymentResponseDto[]
  >([]);
  const [taxonomy, setTaxonomy] = useState<TaxonomyResponseDto[]>([]);
  const [focalSpeciesSearching, setFocalSpeciesSearching] = useState(false);
  // State to store the focal species retrieved from the API
  const [defaultFocalSpecies, setDefaultFocalSpecies] =
    useState<TaxonomyResponseDto | null>(null);
  const [loadingFocalSpecies, setLoadingFocalSpecies] =
    useState<boolean>(false);

  const emptyProject: ProjectCreateDto = {
    project_name: "",
    short_name: "",
    acronym: null,
    description: null,
    org_id: org_id ? Number(org_id) : undefined,
    org_name: "",
    country_id: "",
    // Deliberately empty rather than the browser's zone from Intl: whoever creates the
    // project is frequently not in the cameras' country, and a plausible-but-wrong offset
    // silently shifts every detection in a way no downstream consumer can detect. An empty
    // required field is a far better failure than a confident wrong one.
    timezone: "",
    primary_contact_email: "",
    primary_contact_name: "",
    start_date: null,
    end_date: "",
    objectives: "",
    observation_level: "media",
    species_focus: "multiple",
    focal_species_id: null,
    sensor_layout: "systematic_random",
    sensor_layout_target: null,
    sensors_clustered: false,
    capture_method: "",
    bait_used: "no",
    bait_type: null,
    project_stratified: false,
    stratification_type: null,
    blank_images_removed: "",
    remove_images_containing_humans: false,
    coordinate_precision: null,
    photos_license: "",
    metadata_license: "",
    citation: null,
    comments: null,
    website: null,
    inherit_permissions: true,
    use_default_storage: true,
    storage_url: null,
    container_name: null,
    deployment_schema: {},
    ui_schema: {},
  };
  const [project, setProject] = useState<ProjectCreateDto>(emptyProject);
  const [originalProject, setOriginalProject] = useState<
    ProjectResponseDto | undefined
  >(undefined);
  // The form as last loaded or saved, which unsaved changes are measured against
  // (AKL-1083). Not `originalProject`: the form coerces a loaded project (a null time zone
  // to "", `observation_level` to "media"), so the two differ before anyone types. Null
  // until an existing project has loaded; a new project starts from the empty form.
  const [savedForm, setSavedForm] = useState<ProjectCreateDto | null>(
    existingProject ? null : emptyProject,
  );
  const [sasToken, setSasToken] = useState<string>("");
  const [currentSasToken, setCurrentSasToken] = useState<string>("");
  const [updatingSasToken, setUpdatingSasToken] = useState(false);
  const [canSetSecret, setCanSetSecret] = useState(false);

  // AKL-1083. A SAS token typed and not saved counts: Save is what stores it. The edits
  // belong to the whole project page, so moving between its tabs keeps them unasked; a new
  // project's form lives only at its own address.
  const location = useLocation();
  const hasUnsavedChanges =
    savedForm !== null && (formsDiffer(project, savedForm) || sasToken !== "");
  const { confirmLeave, leaveWithoutAsking } = useUnsavedChanges(
    hasUnsavedChanges,
    existingProject && projectGuid
      ? "/projects/" + projectGuid
      : location.pathname,
  );

  const { t } = useTranslation();
  const [organizationOptions, setOrganizationOptions] = useState<DropDownDto[]>(
    [],
  );

  /*
   * Loads the project details when the project GUID changes.
   */
  useEffect(() => {
    // Set by the cleanup when the route changes under an in-flight request, so a refusal for
    // the project the route has already left cannot land on the one it shows now (Copilot
    // review, cycle 2). Scoped to the 403 branch this ticket added; the success path's
    // setters have the same exposure and always have — a page-wide guard is its own change.
    let cancelled = false;
    const loadProject = async () => {
      try {
        setIsLoading(true);
        // The route component is reused when only `:projectGuid` changes, so a refusal for
        // one project must not stick to the next one (Copilot review, cycle 1).
        setForbidden(false);
        if (projectGuid && existingProject) {
          // The page is reused when the route moves from one project to another (the
          // default export remounts it only between creating and showing a project), so
          // what was typed for the previous project must not carry over: a SAS token left
          // here would count as unsaved and Save could write it to this project's vault.
          // The baseline waits for this project's own load (AKL-1083; Copilot, PR #847
          // rounds 4 and 5).
          setSavedForm(null);
          setSasToken("");
          setUpdatingSasToken(false);
          // "Saved in key vault" is known only for a token saved on this page, so it
          // belongs to the previous project too (Copilot, PR #847 round 6).
          setCurrentSasToken("");
          const tempProject = await getProjectByGuid(projectGuid);
          if (!tempProject) {
            throw new Error("Project not found");
          }
          setProjectId(tempProject.project_id);
          setOrgId(tempProject.org_id);
          const loadedForm = {
            ...tempProject,
            observation_level: "media",
            // A project created before AKL-722 reads back null. Coerce to "" so the picker
            // renders empty and the required-field check prompts the user to choose one —
            // that first save is the intended manual-migration step for legacy projects.
            timezone: tempProject.timezone ?? "",
          };
          setProject(loadedForm);
          setSavedForm(loadedForm);
          setOriginalProject(tempProject);
          await loadOrganizationOptions(
            tempProject.org_id,
            tempProject.org_name,
          );
        } else if (!existingProject && guid) {
          // When creating a new project from an organization page
          try {
            const tempOrganization = await getOrganizationByGuid(guid);
            if (tempOrganization) {
              setOrgId(tempOrganization.org_id);
              const fromOrganization = {
                org_id: tempOrganization.org_id,
                org_name: tempOrganization.org_name,
              };
              setProject((prevState) => ({
                ...prevState,
                ...fromOrganization,
              }));
              // Filled in by the page, not typed: part of the starting form.
              setSavedForm((prevState) =>
                prevState ? { ...prevState, ...fromOrganization } : prevState,
              );
              await loadOrganizationOptions(
                tempOrganization.org_id,
                tempOrganization.org_name,
              );
            } else {
              console.warn("Organization not found for guid:", guid);
              await loadOrganizationOptions(undefined, undefined);
            }
          } catch (orgError) {
            console.error("Error loading organization:", orgError);
            await loadOrganizationOptions(undefined, undefined);
          }
        } else {
          await loadOrganizationOptions(undefined, undefined);
        }
      } catch (error) {
        // The API refuses `GET /projects/guid/{guid}` with a 403 to a user who may not read
        // the project — a deep link, a link a colleague shared. Show the shared no-access
        // state instead of an empty project form (AKL-939). `projectId` was never set, so the
        // effects keyed on it stay idle.
        if (isForbiddenError(error)) {
          if (!cancelled) setForbidden(true);
        } else {
          console.error("Error loading project:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadProject();
    return () => {
      cancelled = true;
    };
  }, [projectGuid, existingProject, guid]);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        if (project_id && existingProject) {
          const temp = await getProjectPermissions(Number(project_id));
          setPerms(temp);
          setCanSetSecret(
            temp.some((permission: any) => permission.name === "SET_SECRET"),
          );
        } else if (!existingProject) {
          // if creating a new project, the user has permission to set the secret
          setCanSetSecret(true);
        }
      } catch (err) {
        console.error("Error loading project permissions:", err);
      }
    };
    if (!profileLoading) {
      loadPermissions();
    }
  }, [project_id, existingProject, profileLoading]);

  /*
   * Load countries when language changes.
   */
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const temp = await getCountries(i18n.language);
        setCountries(temp);
      } catch (error) {
        console.error(t("Error loading countries:"), error);
      }
    };

    loadCountries();
  }, [t]);

  /*
   * Load the valid project timezones once.
   *
   * The list itself is not localized — IANA identifiers are technical values sent back to
   * the API verbatim, unlike the countries list above — so this would ideally run once.
   * `t` is in the dependency array only because the error toast reads it; keeping the array
   * exhaustive is worth one redundant refetch on a language change, and silencing the lint
   * rule to avoid it would hide a genuinely stale closure.
   */
  useEffect(() => {
    const loadTimezones = async () => {
      try {
        setTimezones(await getProjectTimezones());
      } catch (error) {
        // Toast explicitly: the response interceptor does NOT toast 4xx (it only rejects a
        // normalised error), so without this the user faces a required dropdown with no
        // options and no explanation of why it is empty.
        console.error("Error loading project timezones:", error);
        toast.error(t("Failed to load project timezones"));
      } finally {
        setTimezonesLoading(false);
      }
    };

    loadTimezones();
  }, [t]);

  useEffect(() => {
    const loadDeploymentTemplate = async () => {
      try {
        const temp = await getDeploymentTemplates();
        setDeploymentTemplates(temp);
      } catch (error) {
        console.error(t("Error loading deployment templates:"), error);
      }
    };
    loadDeploymentTemplate();
  }, [t]);

  useEffect(() => {
    const loadTaxonomyData = async () => {
      try {
        const response = await fetchTaxonomyData();
        setTaxonomy(response.items);
      } catch (error) {
        console.error(t("Error loading taxonomy data:"), error);
      }
    };
    loadTaxonomyData();
  }, [t]);

  /*
    Load organization options for the dropdown.
  */
  const loadOrganizationOptions = async (
    currentOrgId: number | undefined,
    currentOrgName: string | undefined,
  ) => {
    let tempOrgOptions: DropDownDto[] = [];
    try {
      tempOrgOptions = await getProjectOrganizationOptions();
    } catch (error) {
      console.error("Error loading organizations:", error);
    }
    if (currentOrgId && currentOrgName) {
      //if the current orgId is not present in the organizationOptions, then add it
      if (!tempOrgOptions.some((org) => org.value === currentOrgId)) {
        tempOrgOptions.push({
          value: currentOrgId,
          label: currentOrgName || "",
        });
      }
    }
    setOrganizationOptions(tempOrgOptions);
  };

  // Clear errors when required fields in form data change, if form has been saved at least once.
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      formIsValid();
    }
  }, [
    project.project_name,
    project.org_id,
    project.country_id,
    project.primary_contact_email,
  ]);

  const handleSelectOrganization = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setProject((prevState) => ({
        ...prevState,
        org_id: Number(selectedOption.value),
      }));
      loadOrganizationOptions(
        Number(originalProject?.org_id),
        originalProject?.org_name,
      );
    } else {
      setProject((prevState) => ({
        ...prevState,
        org_id: undefined,
      }));
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { type, name, value, checked } = e.target as HTMLInputElement;

    let inputValue: any = type === "checkbox" ? checked : value;
    if (inputValue === "") {
      inputValue = null;
    }
    setProject((prevState) => ({
      ...prevState,
      [name]: inputValue,
    }));
  };

  /*
   * Cancel: asks first when the form holds unsaved changes (AKL-1083). On Discard the form
   * goes back to its saved state as well, because going back may land on another of this
   * project's tabs, where the edits would otherwise still be waiting.
   */
  const handleCancel = () => {
    confirmLeave(() => {
      if (savedForm) setProject(savedForm);
      setSasToken("");
      // Closes the token editor too: Back may land on another of this project's tabs, and
      // returning to Overview would show an empty editor (Copilot, PR #847 round 6).
      setUpdatingSasToken(false);
      navigate(-1);
    });
  };

  const formIsValid = (): boolean => {
    const newErrors: any = {};

    if (!project.project_name) {
      newErrors.project_name = t("Project name is required");
    }
    if (!project.short_name) {
      newErrors.short_name = t("Project short name is required");
    }
    if (!project.org_id) {
      newErrors.org_id = t("Organization is required");
    }
    if (!project.objectives) {
      newErrors.objectives = t("Objectives are required");
    }
    if (!project.country_id) {
      newErrors.country_id = t("Country is required");
    }
    if (!project.timezone) {
      newErrors.timezone = t("Project timezone is required");
    }
    if (!project.primary_contact_name) {
      newErrors.primary_contact_name = t("Project admin is required");
    }
    // if (!project.observation_level) {
    //   newErrors.observation_level = t("Observation level is required");
    // }
    if (!project.species_focus) {
      newErrors.species_focus = t("Species focus is required");
    }
    if (!project.sensor_layout) {
      newErrors.sensor_layout = t("Sensor layout is required");
    }
    if (project.capture_method === "") {
      newErrors.capture_method = t("Capture method is required");
    }
    if (project.bait_used === "") {
      newErrors.bait_used = t("Bait used is required");
    }
    if (
      (project.bait_used === "some" || project.bait_used === "yes") &&
      project.bait_type === ""
    ) {
      newErrors.bait_type = t("Bait type is required");
    }
    if (project.blank_images_removed === "") {
      newErrors.blank_images_removed = t("Selection is required");
    }
    if (project.photos_license === "") {
      newErrors.photos_license = t("Photos license is required");
    }
    if (project.metadata_license === "") {
      newErrors.metadata_license = t("Metadata license is required");
    }
    // if there is a start and end date, make sure end date is after start date
    if (project.start_date && project.end_date) {
      const startDate = parseLocalDateStringToDate(project.start_date);
      const endDate = parseLocalDateStringToDate(project.end_date);
      if (startDate && endDate && startDate > endDate) {
        newErrors.end_date = t("End date must be after start date");
      }
    }
    // AKL-728: the narrative fields use a counter rather than a hard maxLength, so an
    // over-long value reaches here instead of being silently clipped as it types. The
    // limits come from common/fieldLimits.ts, which mirrors the API's column widths.
    const softLimited: [keyof typeof project, string, number][] = [
      ["description", t("Description"), FIELD_LIMITS.NARRATIVE],
      ["objectives", t("Objectives"), FIELD_LIMITS.NARRATIVE],
      ["comments", t("Comments"), FIELD_LIMITS.NARRATIVE],
      ["citation", t("Citation"), FIELD_LIMITS.NARRATIVE],
      [
        "sensor_layout_target",
        t("Sensor Layout Target"),
        FIELD_LIMITS.LONG_LINE,
      ],
      ["stratification_type", t("Stratification Type"), FIELD_LIMITS.LONG_LINE],
    ];
    // maxLength stops someone *typing* past a hard limit, but it does not trim a value
    // loaded into the input -- so a project stored before this shipped can sit in the form
    // over the cap and fail on save with a bare 422 and nothing pointing at the field.
    // short_name is the only limit this ticket tightened (255 -> Wildlife Insights' 43);
    // project_name and the email both widened, so no stored value can exceed them. They
    // are listed anyway so the check does not have to be revisited if a limit ever
    // narrows again.
    const hardLimited: [keyof typeof project, string, number][] = [
      ["short_name", t("Short Name"), FIELD_LIMITS.SHORT_NAME],
      ["project_name", t("Project Name"), FIELD_LIMITS.PROJECT_NAME],
      ["primary_contact_email", t("Project Admin Email"), FIELD_LIMITS.EMAIL],
    ];

    [...softLimited, ...hardLimited].forEach(([field, label, limit]) => {
      const message = lengthError(
        t,
        label,
        project[field] as string | null | undefined,
        limit,
        i18n.language,
      );
      if (message) newErrors[field] = message;
    });

    if (!project.primary_contact_email) {
      newErrors.primary_contact_email = t("Email is required");
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
        project.primary_contact_email,
      )
    ) {
      newErrors.primary_contact_email = t("Invalid email address");
    }

    const isValidUrl = (urlString: string): boolean => {
      try {
        const url = new URL(urlString);
        // check for things that are hard to find after parsing the uRL
        if (
          urlString.includes("/..") ||
          urlString.includes("/./") ||
          urlString.includes(" ")
        )
          return false;

        if (url.protocol !== "https:") return false;
        if (!url.hostname || url.hostname.startsWith(".")) return false;
        if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(url.hostname)) return false;
        const invalidChars = /[<>#%{}|^~[\]`]/;
        if (
          invalidChars.test(url.pathname) ||
          invalidChars.test(url.search) ||
          invalidChars.test(url.hash)
        ) {
          return false;
        }
        if (url.pathname.split("/").includes("..")) {
          return false;
        }
        const invalidPathSequences = /(^|\/)\.\.($|\/)/;
        if (
          invalidPathSequences.test(url.pathname) ||
          invalidPathSequences.test(url.search) ||
          invalidPathSequences.test(url.hash)
        ) {
          return false;
        }
        return true;
      } catch (err) {
        console.error("URL Error: ", urlString, err);
        return false;
      }
    };

    if (project.website && !isValidUrl(project.website)) {
      newErrors.website = t(
        "Website must be a valid URL beginning with 'https://'.",
      );
    }

    if (!project.use_default_storage) {
      if (!project.storage_url) {
        newErrors.storage_url = t(
          "Storage URL is required for non-default storage.",
        );
      }

      if (project.storage_url && !isValidUrl(project.storage_url)) {
        newErrors.storage_url = t("Storage URL is not a valid URL.");
      }

      if (!project.container_name) {
        newErrors.container_name = t(
          "Container name is required for non-default storage.",
        );
      }

      if (!sasToken && updatingSasToken) {
        newErrors.sas_token = t(
          "SAS token is required for non-default storage.",
        );
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /*
   * Handles the selection of a country from the dropdown.
   */
  const handleSelectCountry = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setProject((prevState) => ({
        ...prevState,
        country_id: selectedOption.value,
      }));
    }
    if (!selectedOption) {
      setProject((prevState) => ({
        ...prevState,
        country_id: "",
      }));
    }
  };

  /*
   * Handles the selection of a project timezone from the dropdown.
   */
  const handleSelectTimezone = (
    selectedOption: { value: string; label: string } | null,
  ) => {
    setProject((prevState) => ({
      ...prevState,
      timezone: selectedOption?.value ?? "",
    }));
  };

  /*
   * Handles the selection of a sensor layout from the dropdown.
   */
  const handleSelectSensorLayout = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setProject((prevState) => ({
        ...prevState,
        sensor_layout: selectedOption.value,
      }));
    }
  };

  /*
   * Handles the selection of a capture method from the dropdown.
   */
  const handleSelectCaptureMethod = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setProject((prevState) => ({
        ...prevState,
        capture_method: selectedOption.value,
      }));
    }
  };

  /*
   * Handles the selection of bait usedfrom the dropdown.
   */
  const handleSelectBaitUsed = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setProject((prevState) => ({
        ...prevState,
        bait_used: selectedOption.value,
      }));
    }
  };

  /*
   * Handles the selection of bait usedfrom the dropdown.
   */
  const handleSelectBaitType = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setProject((prevState) => ({
        ...prevState,
        bait_type: selectedOption.value,
      }));
    }
  };

  /*
   * Handles the selection of blank images removed the dropdown.
   */
  const handleSelectBlankImagesRemoved = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setProject((prevState) => ({
        ...prevState,
        blank_images_removed: selectedOption.value,
      }));
    }
  };

  /*
   * Handles the selection of photo license the dropdown.
   */
  const handleSelectPhotosLicense = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setProject((prevState) => ({
        ...prevState,
        photos_license: selectedOption.value,
      }));
    }
  };

  /*
   * Handles the selection of metadata license the dropdown.
   */
  const handleSelectMetadataLicense = (selectedOption: any) => {
    if (selectedOption && selectedOption.value) {
      setProject((prevState) => ({
        ...prevState,
        metadata_license: selectedOption.value,
      }));
    }
  };

  /*
   * Handle the change of the storage option.
   */
  const handleStorageOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const useDefaultStorage = event.target.value === "true";
    setProject((prevState) => ({
      ...prevState,
      use_default_storage: useDefaultStorage,
    }));
    // if the user selects My Storage and this is new project, prompt the user to update the SAS token
    if (!useDefaultStorage && !currentSasToken) {
      setUpdatingSasToken(true);
    }
  };

  /*
   * Handle the change of the observation level.
   */
  const handleObservationLevelChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setProject((prevState) => ({
      ...prevState,
      observation_level: event.target.value,
    }));
  };

  /*
   * Handle the change of the species focus.
   */
  const handleSpeciesFocusChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.value;
    setProject((prevState) => ({
      ...prevState,
      species_focus: newValue,
      // Clear focal_species related fields if changing to "multiple"
      ...(newValue === "multiple" && {
        focal_species_id: null,
      }),
    }));
  };

  const [schemaLoading, setSchemaLoading] = useState(false);
  /*
   * Handle the change of the schema template.
   */
  const handleSelectSchemaTemplate = async (selectedOption: any) => {
    setSchemaLoading(true);
    if (selectedOption && selectedOption.value) {
      try {
        // Fetch the complete template details by ID
        const templateDetails = await getDeploymentTemplateById(
          selectedOption.value,
        );
        setProject((prevState) => ({
          ...prevState,
          deployment_schema: templateDetails.schema_definition,
          ui_schema: templateDetails.ui_schema, // Use empty object if ui_schema is not provided
        }));
        setSchemaLoading(false);
      } catch (error) {
        toast.error(t("Failed to load deployment template details"));
        setSchemaLoading(false); // Make sure to set loading to false on error
      }
    } else {
      // Handle clearing the selection
      setProject((prevState) => ({
        ...prevState,
        deployment_schema: {},
        ui_schema: {},
      }));
      setSchemaLoading(false); // Set loading to false when selection is cleared
    }
  };

  /*
   * Save the SAS token for the project. The typed token is cleared only once it is stored
   * (or there was none to store): when saving it fails, it stays in the form, still counted
   * as an unsaved change (AKL-1083), so the user can press Save again instead of retyping it
   * (Copilot, PR #847). Never rejects.
   */
  const handleSaveSecret = async (project: ProjectResponseDto) => {
    // only update the secret if the user has permission to set the secret, selected My Storage, has entered a SAS token
    if (project.project_id && project.org_id && sasToken !== "") {
      const secret: SetSecretQueryType = {
        project_id: project.project_id as number,
        secret_name: "Sas",
        secret_value: sasToken,
      };
      let stored = false;
      try {
        const message = await setSecret(secret);
        stored = !message.error;
      } catch (error) {
        console.error("Error saving the SAS token:", error);
      }
      if (!stored) {
        toast.error(t("Failed to save secret"));
        return;
      }
      setCurrentSasToken(sasToken);
      toast.success(t("Secret saved successfully!"));
    }
    setUpdatingSasToken(false);
    setSasToken("");
  };

  /*
   * Create a new project using the form data and create project secret.
   */
  const handleCreateProject = async () => {
    setIsSaving(true);
    try {
      let newproject = {
        ...project,
        coordinate_precision: Number(project.coordinate_precision) || null,
        // start_date as string from the selected start_date
        start_date: project.start_date ? project.start_date : null,
        // end_date as string from the selected end_date
        end_date: project.end_date ? project.end_date : null,
      };
      let temp = await createProject(newproject);
      if (temp) toast.success(t("Project created successfully!"));

      if (canSetSecret && !project.use_default_storage) {
        handleSaveSecret(temp);
      }
      // Saved: the form's contents are the new project, so nothing is lost by leaving.
      leaveWithoutAsking(() =>
        navigate("/projects/" + temp.guid + "/overview"),
      );
    } catch (error: any) {
      toast.error(
        t("Failed to create project:") + error.data?.detail.toString(),
      );
    } finally {
      setIsSaving(false);
      setUpdatingSasToken(false);
      setSasToken("");
    }
  };

  /*
   * Update an existing project using the form data and update project secret.
   */
  const handleUpdateProject = async () => {
    setIsSaving(true);
    try {
      let projectUpdate = {
        ...project,
        coordinate_precision: Number(project.coordinate_precision) || null,
        start_date: project.start_date,
        end_date: project.end_date,
      };
      if (project.use_default_storage) {
        // if Default storage is selected, override the storage URL and SAS token with original values
        projectUpdate.storage_url = originalProject?.storage_url || "";
      }

      let temp = await updateProject(Number(project_id), projectUpdate);
      // Same null-coercion as the initial load: the response type is honestly nullable, the
      // form state is not.
      const updatedForm = { ...temp, timezone: temp.timezone ?? "" };
      setProject(updatedForm);
      setSavedForm(updatedForm);
      toast.success(t("Project updated successfully!"));
      setOriginalProject(temp);
      if (canSetSecret && !project.use_default_storage) {
        // Clears the typed token itself, and only once it is stored.
        await handleSaveSecret(temp);
      } else {
        // Default storage keeps no token, so a typed one has nothing to be saved to.
        setUpdatingSasToken(false);
        setSasToken("");
      }
    } catch (error) {
      // The project was not updated: a typed token stays, like the other edits, so Save
      // can be pressed again (AKL-1083).
      console.error(error);
      toast.error(t("Failed to update project"));
    } finally {
      setIsSaving(false);
    }
  };

  /*
   * Handle the form submission by creating or updating the project.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let canSave = formIsValid();
    if (!canSave) {
      return;
    }
    if (!existingProject) {
      await handleCreateProject();
    } else {
      await handleUpdateProject();
    }
  };

  function handleSelectSubPage(e: string) {
    navigate("/projects/" + projectGuid + "/" + e);
  }

  /**
   * An applied CSV import put a deployment template on this project (AKL-815: the Wildlife
   * Insights deployments format does that to a project without one). The template lives in
   * `originalProject` and the form state, both loaded with the page, so both are stale from
   * that moment — the Deployments tab's import dialog would list no template columns and
   * offer a download without the new columns until a reload. Re-read the project and
   * refresh `originalProject` whole; on the form state, only the two template fields, so an
   * edit the user has not saved on another tab survives. A failed re-read is logged and
   * ignored: the import itself succeeded, and the next page load shows the template.
   */
  const handleTemplateApplied = useCallback(async () => {
    if (!projectGuid) return;
    try {
      const fresh = await getProjectByGuid(projectGuid);
      if (!fresh) return;
      setOriginalProject(fresh);
      setProject((prev) => ({
        ...prev,
        deployment_schema: fresh.deployment_schema,
        ui_schema: fresh.ui_schema,
      }));
      // The import changed the saved template, not the user: not an unsaved change.
      setSavedForm((prev) =>
        prev
          ? {
              ...prev,
              deployment_schema: fresh.deployment_schema,
              ui_schema: fresh.ui_schema,
            }
          : prev,
      );
    } catch (error) {
      console.error(
        "Error re-reading the project after an import applied a template:",
        error,
      );
    }
  }, [projectGuid]);

  // I want to keep this separate because we will eventually be removing this to
  //make way for the custom schema data business logic

  const [fetchingDeployments, setFetchingDeployments] = useState(false);

  /**
   * Load every deployment of a project into `projectDeployments`.
   *
   * Declared before the effects that call it (naming it in a dependency array before its
   * declaration would be a temporal-dead-zone error at render). Shared by the two fetch
   * effects below and by the migration's post-apply refresh, which is why it takes the id
   * rather than closing over state. Memoised with no dependencies: besides its argument it
   * touches only state setters (stable by contract) and the module-level `getDeployments`
   * import, none of which the exhaustive-deps rule asks for — so listing it in the effects
   * widens nothing.
   * @param projectId the project whose deployments to load
   */
  const loadProjectDeployments = useCallback(async (projectId: number) => {
    setFetchingDeployments(true);
    try {
      const deploymentQuery: DeploymentSearchQueryType = {
        project_id: projectId,
        all_deployments: true,
      };
      const deploymentResult = await getDeployments(deploymentQuery);
      setProjectDeployments(deploymentResult.deployments || []);
    } catch (error) {
      console.error("Error fetching project deployments:", error);
    } finally {
      setFetchingDeployments(false);
    }
  }, []);

  // Fetch when project_id changes
  useEffect(() => {
    if (!project_id || fetchingDeployments) return;
    loadProjectDeployments(project_id);
  }, [project_id, loadProjectDeployments]);

  // Fetch when navigating to the overview tab
  useEffect(() => {
    if (
      subpage !== "overview" ||
      !existingProject ||
      fetchingDeployments ||
      !project_id
    )
      return;
    loadProjectDeployments(project_id);
  }, [subpage, existingProject, project_id, loadProjectDeployments]);

  // Handler for loading taxonomy options as user types
  const handleTaxonomySearch = debounce(
    async (inputValue: string, callback: (options: any[]) => void) => {
      if (!inputValue || inputValue.length < 2) {
        callback([]);
        return;
      }

      setFocalSpeciesSearching(true);
      try {
        const results = await searchTaxonomy(inputValue);
        const options = results.map((tax: TaxonomyResponseDto) => {
          // Create a readable common name or fallback to scientific name
          const commonName =
            tax.common_name_english ||
            (tax.genus && tax.species
              ? `${tax.genus} ${tax.species}`
              : tax.scientific_name || "Unknown species");

          return {
            value: tax.id, // Using id as the value
            label:
              tax.genus && tax.species
                ? `${tax.common_name_english || ""} (${tax.genus} ${
                    tax.species
                  })`
                : tax.common_name_english || tax.scientific_name || "Unknown",
            taxonomyData: {
              id: tax.id,
              class_: tax.class_,
              family: tax.family,
              genus: tax.genus,
              species: tax.species,
              order: tax.order,
              common_name_english: commonName,
              scientific_name:
                tax.scientific_name ||
                `${tax.genus || ""} ${tax.species || ""}`.trim(),
              unique_identifier: tax.unique_identifier,
            },
          };
        });
        callback(options);
      } catch (error) {
        console.error("Error searching taxonomy:", error);
        callback([]);
      } finally {
        setFocalSpeciesSearching(false);
      }
    },
    400,
  );

  // Handle selection of focal species
  const onSelectFocalSpecies = (selectedOption: any) => {
    if (selectedOption) {
      // Store the common_name_english in the focal_species field
      handleChange({
        target: {
          name: "focal_species_id",
          value: selectedOption.taxonomyData.unique_identifier || "",
        },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  // Effect to fetch focal species data when the focal_species_id changes
  useEffect(() => {
    const fetchFocalSpecies = async () => {
      if (project.focal_species_id) {
        try {
          setLoadingFocalSpecies(true);
          const species = await getTaxonomyById(project.focal_species_id);
          setDefaultFocalSpecies(species);
        } catch (error) {
          console.error("Error fetching focal species:", error);
          setDefaultFocalSpecies(null);
        } finally {
          setLoadingFocalSpecies(false);
        }
      } else {
        setDefaultFocalSpecies(null);
      }
    };

    fetchFocalSpecies();
  }, [project.focal_species_id]);

  // Who may see the migration button. In create mode the organisation options double as
  // the answer to "may this user create a project anywhere"; in target mode it is the
  // three project permissions the API demands.
  const canMigrate = canOpenWildlifeInsightsMigration({
    existingProject,
    isAdmin: profile?.is_admin ?? false,
    organizationOptionCount: organizationOptions.length,
    perms,
  });

  /*
   * Deep link: `?migrate=wi` opens the modal once the gate is known to pass, then drops the
   * parameter so a refresh does not reopen it. When the gate fails the parameter is left
   * alone and nothing happens. `canMigrate` only turns true after the organisation options
   * or the permissions have loaded, so this waits for them without a separate flag.
   */
  useEffect(() => {
    if (!canMigrate || searchParams.get("migrate") !== "wi") return;
    setMigrationOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("migrate");
    setSearchParams(next, { replace: true });
  }, [canMigrate, searchParams, setSearchParams]);

  /**
   * Refresh what the page shows after a migration writes.
   *
   * In target mode the deployments list is stale (the migration creates deployments). In
   * create mode nothing on the form needs refreshing; the job is kept so `handleMigrationClose`
   * can take the user to what was created once they have read the report.
   * @param job the applied migration job
   */
  const handleMigrationApplied = (job: MigrationJobDto) => {
    if (existingProject && project_id) {
      loadProjectDeployments(project_id);
    } else {
      setAppliedMigration(job);
    }
  };

  /**
   * Close the modal and, after a create-mode migration, move on: one project created or
   * matched → its overview; several → the projects list; none → stay on the form. If the
   * created project cannot be fetched for its guid, the list is the safe fallback.
   */
  const handleMigrationClose = async () => {
    setMigrationOpen(false);
    if (existingProject || !appliedMigration) return;
    const job = appliedMigration;
    setAppliedMigration(null);
    const ids = migrationLandingProjectIds(job.result);
    if (ids.length === 0) return;
    // The migration created or matched the projects, so an unsaved form is not asked
    // about: the user chose the migration over it (AKL-1083).
    if (ids.length > 1) {
      leaveWithoutAsking(() => navigate("/projects"));
      return;
    }
    const created = await getProject(ids[0]);
    leaveWithoutAsking(() =>
      navigate(
        created?.guid ? "/projects/" + created.guid + "/overview" : "/projects",
      ),
    );
  };

  return isLoading ? (
    <div className="flex flex-row w-full">
      <div>
        <SideMenu />
      </div>
      <div className="flex flex-col items-center w-full mt-8">
        <BeatLoader
          color={tailwindClassnames.skyBlueDark}
          loading={true}
          size={15}
          margin={5}
        />
      </div>
    </div>
  ) : forbidden ? (
    <div className="flex flex-row w-full">
      <div>
        <SideMenu />
      </div>
      <div className="flex flex-col w-full mx-8">
        <NoAccess />
      </div>
    </div>
  ) : (
    <div className="flex flex-row w-full">
      <div>
        <SideMenu />
      </div>
      <div className="flex flex-col w-full mx-8">
        <ProjectDetails
          projectName={originalProject?.project_name || ""}
          perms={perms}
          existingProject={existingProject}
          subPage={subpage}
          onSelectSubPage={handleSelectSubPage}
          onMigrate={canMigrate ? () => setMigrationOpen(true) : undefined}
        />
        {/* Mounted only while open so each opening reads the form's current Organization
            and Timezone (the modal captures its starting values at mount), and so closing
            cancels any in-flight polling. */}
        {migrationOpen &&
          (existingProject ? (
            <MigrateFromWildlifeInsights
              isOpen={migrationOpen}
              onClose={handleMigrationClose}
              // Target mode: the second pass, into this project.
              projectId={project_id ?? null}
              projectName={originalProject?.project_name}
              orgId={originalProject?.org_id}
              onApplied={handleMigrationApplied}
            />
          ) : (
            <MigrateFromWildlifeInsights
              isOpen={migrationOpen}
              onClose={handleMigrationClose}
              // Create mode, seeded from whatever the form already has.
              projectId={null}
              orgId={project.org_id || undefined}
              organizations={organizationOptions.map((option) => ({
                org_id: Number(option.value),
                org_name: option.label,
              }))}
              initialTimezone={project.timezone || undefined}
              onApplied={handleMigrationApplied}
            />
          ))}
        {(subpage === "overview" || !existingProject) && (
          <ProjectDetailsOverview
            projectId={project_id}
            orgId={originalProject?.org_id || Number(org_id)}
            profile={profile}
            formData={project}
            deploymentTemplates={deploymentTemplates}
            deployments={projectDeployments}
            perms={perms}
            canSetSecret={canSetSecret}
            sasToken={sasToken}
            secretIsSet={!!currentSasToken}
            updatingSasToken={updatingSasToken}
            setSasToken={setSasToken}
            setUpdatingSasToken={setUpdatingSasToken}
            onSelectOrganization={handleSelectOrganization}
            onSelectCountry={handleSelectCountry}
            timezones={timezones}
            timezonesLoading={timezonesLoading}
            onSelectTimezone={handleSelectTimezone}
            onStorageOptionChange={handleStorageOptionChange}
            onObservationLevelChange={handleObservationLevelChange}
            onSpeciesFocusChange={handleSpeciesFocusChange}
            onSelectSensorLayout={handleSelectSensorLayout}
            onSelectCaptureMethod={handleSelectCaptureMethod}
            onSelectBaitUsed={handleSelectBaitUsed}
            onSelectBaitType={handleSelectBaitType}
            onSelectBlankImagesRemoved={handleSelectBlankImagesRemoved}
            onSelectPhotosLicense={handleSelectPhotosLicense}
            onSelectMetadataLicense={handleSelectMetadataLicense}
            onSelectSchemaTemplate={handleSelectSchemaTemplate}
            schemaLoading={schemaLoading}
            onCancel={handleCancel}
            onSave={handleSubmit}
            isSaving={isSaving}
            // A typed SAS token is saved by Save too, so it enables Save on its own: it
            // counts as an unsaved change (AKL-1083), and Save is the only way to keep it.
            // Off while a save is running too, so a second click cannot start
            // overlapping writes (Copilot, PR #847 round 7).
            saveDisabled={
              isSaving ||
              (JSON.stringify(project) === JSON.stringify(originalProject) &&
                sasToken === "")
            }
            existingProject={existingProject}
            organizationOptions={organizationOptions}
            countries={countries}
            errors={errors}
            onChange={handleChange}
            taxonomy={taxonomy}
            onSelectFocalSpecies={onSelectFocalSpecies}
            focalSpeciesSearching={focalSpeciesSearching}
            onTaxonomySearch={handleTaxonomySearch}
            loadingFocalSpecies={loadingFocalSpecies}
            defaultFocalSpecies={defaultFocalSpecies}
          />
        )}
        {subpage === "users" && (
          <ManageProjectUsers
            projectId={Number(project_id)}
            projectName={originalProject?.project_name || ""}
            perms={perms}
          />
        )}
        {subpage === "deployments" && deploymentGuid !== undefined && (
          <ManageDeploymentDetails
            projectId={Number(project_id)}
            projectGuid={projectGuid as string}
            orgId={originalProject?.org_id || Number(org_id)}
            perms={perms}
            existingDeployment={deploymentGuid !== "add"}
            // AKL-815: an import from the Add page can put a deployment template on the
            // project; the list this page then leaves for reads the template from
            // `originalProject`, so it is re-read here (see `handleTemplateApplied`).
            onTemplateApplied={handleTemplateApplied}
          />
        )}
        {subpage === "deployments" && deploymentGuid === undefined && (
          <ManageProjectDeployments
            projectId={Number(project_id)}
            projectGuid={projectGuid as string}
            orgId={originalProject?.org_id || Number(org_id)}
            perms={perms}
            // AKL-815: the CSV import lists the columns this project's deployment template
            // adds. `originalProject` is set, with `org_id`, in the same load (`loadProject`
            // above) before any tab renders (the `isLoading` spinner), refreshed after a
            // successful save, and re-read when an applied import puts a template on the
            // project (`handleTemplateApplied`) — so picking a template on the Project
            // Details tab, or importing one, reaches this prop, and an empty template here
            // means "none", never "not loaded yet".
            deploymentSchema={originalProject?.deployment_schema}
            uiSchema={originalProject?.ui_schema}
            onTemplateApplied={handleTemplateApplied}
          />
        )}
        {subpage === "locations" && (
          <ManageProjectLocations
            projectId={Number(project_id)}
            orgId={originalProject?.org_id || Number(org_id)}
            perms={perms}
          />
        )}
        {subpage === "ai-mappings" && (
          <ManageProjectAiMappings
            projectId={Number(project_id)}
            perms={perms}
          />
        )}
      </div>
    </div>
  );
};

/**
 * The project page, remounted whenever it switches between creating a project and showing
 * one (AKL-1083; Copilot, PR #847 round 5).
 *
 * React reuses one instance across the Add routes and the existing-project routes, because
 * they render the same component. The state one mode leaves would then be the other's
 * starting point:
 * - a SAS token typed on the Add form would carry into the project a migration created;
 * - Back from a just-created project would show the Add form filled in with that project
 *   and counted as saved, so Save would create it a second time.
 * Keying on the mode gives each mode a fresh page. A new project's key also carries the
 * organisation its Add route names, so `/addProject` and `/organizations/:guid/addProject`
 * (or two organisations) each start with an empty form too (Copilot, PR #847 round 7).
 * Moving between one project's tabs, or into one of its deployments, stays in "existing"
 * mode and keeps the form's edits.
 * @param props.existingProject whether the route shows an existing project
 * @returns the page for that mode
 */
const ManageProjectDetails: React.FC<ManageProjectDetailsProps> = (props) => {
  const { guid } = useParams();
  return (
    <ManageProjectDetailsPage
      key={props.existingProject ? "existing" : `new:${guid ?? ""}`}
      {...props}
    />
  );
};

export default ManageProjectDetails;
